import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  Renderer2,
  signal,
  TemplateRef,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { MenuItem } from 'primeng/api';

@Component({
  selector: 'app-responsive-tablist',
  templateUrl: './responsive-tablist.component.html',
  styleUrl: './responsive-tablist.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ResponsiveTablistComponent implements AfterViewInit, OnDestroy {
  @Input() value: string;
  @Input() key: string = 'id';
  @Input() tabs: MenuItem[] = [];
  @Input() set extraMenuItems(items: MenuItem[]) {
    this._extraMenuItems.set(items);
  }
  @Output() activeResultsTabChanged = new EventEmitter<MenuItem>();

  @ContentChild('tabTpl') tabTpl: TemplateRef<any>;
  @ViewChild('containerRef') private containerRef: ElementRef<HTMLDivElement>;
  @ViewChildren('tab', { read: ElementRef }) private readonly tabElements: QueryList<ElementRef>;

  public containerVisibleWidth = signal<number>(0);
  public tabElementsWidth = signal<number[]>([]);
  public tabsMoreList = signal<MenuItem[]>([]);
  public _extraMenuItems = signal<MenuItem[]>([]);
  public moreBtnLeftPosition = signal<number>(0);
  public isMoreOpen = signal<boolean>(false);
  public prevInnerWidth = signal<number>(0);
  public scrollableTabs = signal<boolean>(true);

  public completeTabsMoreList = computed(() =>
    [
      ...this.tabsMoreList().map((item) => {
        return {
          ...item,
          useTpl: true
        };
      }),
      !!this.tabsMoreList().length && !!this._extraMenuItems().length ? { separator: true } : undefined,
      ...(this._extraMenuItems() || undefined)
    ].filter((item) => !!item)
  );

  private readonly tabBtnsGap = 0;
  private readonly moreBtnReservedWidth = 40;
  private readonly LG = 992;
  private readonly delay = 50;

  private resizeObserver: ResizeObserver;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private renderer: Renderer2
  ) {}

  public ngAfterViewInit(): void {
    // defining the actual width of each tab
    const tabsListWidth: number[] = [];
    this.tabElements.forEach((tabEl) => {
      tabsListWidth.push(tabEl.nativeElement.offsetWidth);
    });

    // update the array with all the tabs width numbers,
    // stored inside this.tabElementsWidth
    this.tabElementsWidth.set([...tabsListWidth]);

    this.resizeObserver = new ResizeObserver((entries) => {
      const tablistNavButons = Array.from(this.containerRef['rootEl'].getElementsByClassName('p-tablist-nav-button'));

      if (
        window.innerWidth < this.prevInnerWidth() - this.delay ||
        window.innerWidth > this.prevInnerWidth() + this.delay
      ) {
        if (window.innerWidth < this.LG) {
          tablistNavButons.forEach((button) => {
            this.renderer.addClass(button, 'hidden');
            this.renderer.removeClass(button, 'visible');
          });

          this.scrollableTabs.set(false);
          for (const entry of entries) {
            if (entry.contentBoxSize) {
              const contentBoxSize = entry.contentBoxSize[0];

              // Math.ceil is necessary to round up and return
              // the smallest integer for the size of observed element
              this.containerVisibleWidth.set(Math.ceil(contentBoxSize.inlineSize));

              // invoke the functions which calculates tabs visibility
              // and sets data to the list of hidden tabs
              this.calculateVisibility();
            }
          }
        } else {
          // if the screen is larger than 992px, reset the data
          // and set the tabs to be visible
          this.tabsMoreList.set([]);
          this.moreBtnLeftPosition.set(0);
          this.isMoreOpen.set(false);
          this.scrollableTabs.set(true);

          tablistNavButons.forEach((button) => {
            this.renderer.addClass(button, 'visible');
            this.renderer.removeClass(button, 'hidden');
          });

          this.tabElements.forEach((tabEl) => {
            this.renderer.removeClass(tabEl.nativeElement, 'hidden');
            this.renderer.addClass(tabEl.nativeElement, 'visible');
          });
        }
        this.prevInnerWidth.set(window.innerWidth);
      }
    });

    // adding ResizeObserver to the observed container
    this.resizeObserver.observe(this.containerRef['rootEl']);
  }

  ngOnDestroy(): void {
    this.resizeObserver.unobserve(this.containerRef['rootEl']);
    this.resizeObserver.disconnect();
  }

  private calculateVisibility(): void {
    const activeBar = this.containerRef['rootEl'].getElementsByClassName('p-tablist-active-bar')[0];

    // variable for actual visible tabs width + gaps between tabs calculations
    // to define if the next tab has a free space to be visible or not
    let visibleElementsWidth = 0;

    // variable for the list of hidden tabs which will be
    // put to this.tabsMoreList
    const tabsMoreData: MenuItem[] = [];

    // variable which works as a flag and changes to false with the
    // first hidden tab during tabElements iteration
    let isVisible = true;

    this.tabElements.forEach((tabEl, i) => {
      // necessary gap after the tab
      const gapWidth = i === this.tabElements.length - 1 ? 0 : this.tabBtnsGap;

      // visibleElementsWidth will be increased by
      // the corresponding width of the element + gapWidth
      visibleElementsWidth += this.tabElementsWidth()[i] + gapWidth;

      // calculates how much space is necessary for all the previous
      // tabs + the next tab or button "More"
      const visibleSpaceWidth =
        i !== this.tabElements.length - 1 ? visibleElementsWidth + this.moreBtnReservedWidth : visibleElementsWidth;

      // show the active bar if active tab is not hidden
      if (tabEl.nativeElement.classList.contains('p-tab-active')) {
        this.renderer.addClass(activeBar, 'visible');
        this.renderer.removeClass(activeBar, 'hidden');
      }

      // compare if container's actual width is enough to show all
      // the elements that need space equal to visibleSpaceWidth width
      if (visibleSpaceWidth <= this.containerVisibleWidth() && isVisible) {
        // adjust class names for visible tabs
        this.renderer.removeClass(tabEl.nativeElement, 'hidden');
        this.renderer.addClass(tabEl.nativeElement, 'visible');
      } else {
        if (isVisible) {
          // calculate left property for button "More" which has
          // absolute position
          this.moreBtnLeftPosition.set(
            this.tabElementsWidth()
              .slice(0, i)
              .reduce((acc, item) => item + acc, 0) +
              this.tabBtnsGap * i
          );

          // set isVisible to false for the first hidden tab
          isVisible = false;
        }

        // adjust class names for hidden tabs
        this.renderer.removeClass(tabEl.nativeElement, 'visible');
        this.renderer.addClass(tabEl.nativeElement, 'hidden');

        // hide the active bar if active tab is hidden
        if (tabEl.nativeElement.classList.contains('p-tab-active')) {
          this.renderer.addClass(activeBar, 'hidden');
          this.renderer.removeClass(activeBar, 'visible');
        }

        // update tabsMoreData with the new hidden tab's data
        tabsMoreData.push({ ...this.tabs[i], command: () => this.handleActiveResultsTabChanged(this.tabs[i]) });
      }
    });

    // update this.tabsMoreList with the list of hidden tabs
    this.tabsMoreList.set(tabsMoreData);
    this.cdr.detectChanges();
  }

  handleActiveResultsTabChanged(tab: MenuItem) {
    this.activeResultsTabChanged.emit(tab);
    if (window.innerWidth < this.LG) {
      setTimeout(() => {
        this.calculateVisibility();
      });
    }
  }
}
